import _pick from 'lodash/pick'
import _set from 'lodash/set'
import { OverlayData } from '../components/annotations/overlays/Overlay'
import {
  OverlayActionCall,
  TimerActionCall,
  TimeType,
  VariableActionCall,
} from '../contexts/annotations'
import { formatTimer } from './index'

export interface OverlaysMap {
  [key: string]: OverlayData
}

export const OVERLAY_DISPOSE_TIMEOUT = 2

export function parseVariables(
  actions: VariableActionCall[],
  timeInSeconds: number,
): Map<string, string> {
  const variables = new Map<
    string,
    {
      value: string | number
      precision?: number
      type: 'double' | 'long' | 'string'
    }
  >()

  try {
    actions.map((action) => {
      if (action.offset / 1000 > timeInSeconds) return

      if (action.type === 'set_variable') {
        variables.set(action.data.name, {
          value: action.data.value,
          type: action.data.type,
          precision: action.data?.double_precision || 0,
        })
      }

      if (action.type === 'increment_variable') {
        const currentVariable = variables.get(action.data.name)

        if (
          !currentVariable ||
          isNaN(+currentVariable?.value) ||
          currentVariable.type === 'string'
        )
          return

        currentVariable.value =
          +currentVariable.value + (action.data.amount || 0)

        variables.set(action.data.name, currentVariable)
      }
    })

    const result: Map<string, string> = new Map()

    variables.forEach((variable, key) => {
      const precision = variable.precision && Math.pow(10, variable.precision)

      const formattedValue = precision
        ? (Math.round(+variable.value * precision) / precision).toFixed(
            variable.precision || 0,
          )
        : variable.value + ''
      result.set(key, formattedValue)
    })

    return result
  } catch (e) {
    // tslint:disable-next-line: no-console
    console.error('Failed to parse variables', e)
  }

  return new Map()
}

export function parseTimers(
  actions: TimerActionCall[],
  timeInSeconds: number,
): Map<string, string> {
  const timers = new Map<
    string,
    {
      isStarted: boolean
      currentIntervalTime: number
      total: number
      format: string
      step: number
      direction: 'up' | 'down'
      cap?: number
    }
  >()
  const timerValues = new Map<string, string>()

  try {
    actions.forEach((action) => {
      if (action.offset / 1000 > timeInSeconds) return null

      const timer = timers.get(action.data.name)

      switch (action.type) {
        case 'create_timer': {
          const timerDetails = {
            isStarted: false,
            currentIntervalTime: 0,
            total: action.data.start_value || 0,
            format: action.data.format || 's',
            step: action.data.step || 1000,
            direction: action.data.direction || 'up',
            cap: action.data.cap_value,
          }
          timers.set(action.data.name, timerDetails)
          break
        }
        case 'start_timer':
          if (!timer) return

          timer.isStarted = true
          timer.currentIntervalTime = timeInSeconds * 1000 - action.offset
          timers.set(action.data.name, timer)
          break
        case 'pause_timer':
          if (!timer) return

          if (timer.isStarted === false) break // ignore pause actions while paused
          timer.isStarted = false
          timer.total =
            timer.total +
            (timer.currentIntervalTime -
              (timeInSeconds * 1000 - action.offset)) *
              (timer.direction === 'up' ? 1 : -1)
          timer.total = timer.cap
            ? Math.min(timer.total, timer.cap)
            : timer.total

          timer.currentIntervalTime = 0
          timers.set(action.data.name, timer)
          break
        case 'adjust_timer':
          if (!timer) return
          timer.total = action.data.value || 0
          timer.total = timer.cap
            ? Math.min(timer.total, timer.cap)
            : timer.total
          if (timer.isStarted) {
            timer.currentIntervalTime = timeInSeconds * 1000 - action.offset
          } else {
            timer.currentIntervalTime = 0
          }

          timers.set(action.data.name, timer)

          break
        case 'skip_timer':
          if (!timer) return

          timer.total = timer.total + (action?.data?.value || 0)
          timers.set(action.data.name, timer)
          break
      }
    })

    timers.forEach((value, key) => {
      let timerTotalTime =
        (value.total +
          value.currentIntervalTime * (value.direction === 'up' ? 1 : -1)) /
        1000

      if (value.cap !== undefined) {
        timerTotalTime =
          value.direction === 'up'
            ? Math.min(timerTotalTime, value.cap / 1000)
            : Math.max(timerTotalTime, value.cap / 1000)
      }
      if (value.step && timerTotalTime % (value.step / 1000) !== 0) {
        timerTotalTime = timerTotalTime - (timerTotalTime % (value.step / 1000))
      }

      timerValues.set(
        key,
        formatTimer(timerTotalTime, value.format as TimeType),
      )
    })
  } catch (e) {
    // tslint:disable-next-line: no-console
    console.error('Failed to parse timer', e)
  }

  return timerValues
}

/*
    In order for the react-spring transition to animate (in or out) the Overlay element needs to be alive.
    When seeking, this acts like a delta that allows the overlays to animate. If seeking in between the time
    an animation should start and OVERLAY_DISPOSE_TIMEOUT seconds before, the animation will happen otherwise
    just display the overlay wihtout any animation.
    This should be big enough to allow overlays to finish rendering animations;
 */
export function parseOverlays(
  overlays: OverlayActionCall[],
  currentTime: number,
): OverlaysMap {
  const mappedOverlays: OverlaysMap = {}

  overlays.map((overlay) => {
    const overlayID = overlay.data.custom_id || overlay.id
    const offsetInSeconds = overlay.offset / 1000

    if (offsetInSeconds > currentTime) {
      // Current video time is before the overlay should appear
      return
    }

    if (
      overlay.type === 'show_overlay' &&
      overlay.data?.duration &&
      !overlay.data?.repeat_interval &&
      offsetInSeconds +
        (overlay.data?.duration / 1000 || 0) +
        OVERLAY_DISPOSE_TIMEOUT <
        currentTime
    ) {
      // Current video time is after the visible period of an overlay with duration

      return
    }

    if (
      overlay.type === 'hide_overlay' &&
      (mappedOverlays[overlayID]?.animations?.hide?.animateout_duration || 0) +
        offsetInSeconds +
        OVERLAY_DISPOSE_TIMEOUT <
        currentTime
    ) {
      // Current video time is after hide overlay annotation time
      delete mappedOverlays[overlayID]
    }

    if (['show_overlay', 'reshow_overlay'].includes(overlay.type)) {
      let data = overlay.data

      if (overlay.type === 'reshow_overlay') {
        // If overlay already visible don't reanimate
        if (mappedOverlays[overlayID]) return

        const customOverlay = overlays.find(
          ({ data: customData, type }) =>
            customData.custom_id === data.custom_id && type === 'show_overlay',
        )

        if (customOverlay) {
          data = customOverlay.data
        }
      }

      mappedOverlays[overlayID] = _pick(data, [
        'custom_id',
        'svg_url',
        'position',
        'size',
        'variables',
        'ad_data',
        'repeat_interval',
        'delay',
        'duration',
        'opacity',
        'timer',
        'remove',
      ]) as unknown as OverlayData

      if (offsetInSeconds <= currentTime) {
        _set(
          mappedOverlays[overlayID],
          'animations.show',
          _pick(data, ['animatein_type', 'animatein_duration']),
        )
      }

      if (
        offsetInSeconds + OVERLAY_DISPOSE_TIMEOUT <= currentTime &&
        mappedOverlays?.[overlayID]?.animations?.show?.animatein_duration
      ) {
        // If seeking OVERLAY_DISPOSE_TIMEOUT seconds after show animation don't animate in
        delete mappedOverlays[overlayID].animations?.show?.animatein_duration
      }

      if (
        overlay.type === 'show_overlay' &&
        'duration' in data &&
        data.duration &&
        +offsetInSeconds + data.duration / 1000 <= currentTime
      ) {
        _set(
          mappedOverlays[overlayID],
          'animations.hide',
          _pick(data, ['animateout_type', 'animateout_duration']),
        )
      }
    }

    // Overlay hide annotation
    if (['hide_overlay'].includes(overlay.type)) {
      if (offsetInSeconds <= currentTime) {
        _set(
          mappedOverlays[overlayID],
          'animations.hide',
          _pick(overlay.data, ['animateout_type', 'animateout_duration']),
        )
      }
    }
  })

  return mappedOverlays
}
